import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, 
  Heart, 
  Star, 
  Search,
  Shirt,
  ArrowRight,
  Tag
} from 'lucide-react';

const CheckoutPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock product data
  const products = [
    {
      id: 1,
      name: "Premium Cotton T-Shirt",
      price: 29.99,
      originalPrice: 39.99,
      rating: 4.8,
      reviews: 124,
      image: "👕",
      category: "tops",
      fit: "Perfect Fit",
      colors: ["White", "Black", "Navy"],
      sizes: ["S", "M", "L", "XL"]
    },
    {
      id: 2,
      name: "Slim Fit Jeans",
      price: 79.99,
      originalPrice: 99.99,
      rating: 4.6,
      reviews: 89,
      image: "👖",
      category: "bottoms",
      fit: "Tailored",
      colors: ["Blue", "Black"],
      sizes: ["30", "32", "34", "36"]
    },
    {
      id: 3,
      name: "Casual Blazer",
      price: 129.99,
      originalPrice: 159.99,
      rating: 4.9,
      reviews: 67,
      image: "🧥",
      category: "outerwear",
      fit: "Perfect Fit",
      colors: ["Navy", "Gray"],
      sizes: ["S", "M", "L"]
    },
    {
      id: 4,
      name: "Formal Dress Shirt",
      price: 59.99,
      originalPrice: 79.99,
      rating: 4.7,
      reviews: 156,
      image: "👔",
      category: "tops",
      fit: "Tailored",
      colors: ["White", "Blue", "Pink"],
      sizes: ["S", "M", "L", "XL"]
    }
  ];

  const categories = [
    { id: 'all', name: 'All', icon: '🛍️' },
    { id: 'tops', name: 'Tops', icon: '👕' },
    { id: 'bottoms', name: 'Bottoms', icon: '👖' },
    { id: 'outerwear', name: 'Outerwear', icon: '🧥' },
    { id: 'accessories', name: 'Accessories', icon: '👜' }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-semibold text-gray-900 mb-4">Shop</h1>
          <p className="text-xl text-gray-600">Browse products with AI-powered fit recommendations</p>
        </motion.div>

        {/* Search and Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card-elevated p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="card-elevated p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group"
            >
              {/* Product Image */}
              <div className="text-6xl text-center mb-4 group-hover:scale-110 transition-transform duration-300">
                {product.image}
              </div>

              {/* Product Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 text-lg">{product.name}</h3>
                
                {/* Price */}
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-gray-900">${product.price}</span>
                  <span className="text-sm text-gray-500 line-through">${product.originalPrice}</span>
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                    {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                  </span>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium">{product.rating}</span>
                  </div>
                  <span className="text-sm text-gray-500">({product.reviews} reviews)</span>
                </div>

                {/* Fit Badge */}
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-600 font-medium">{product.fit}</span>
                </div>

                {/* Colors */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Colors:</span>
                  <div className="flex gap-1">
                    {product.colors.map((color, i) => (
                      <div
                        key={i}
                        className="w-4 h-4 rounded-full border border-gray-200"
                        style={{ backgroundColor: color.toLowerCase() }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Sizes */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Sizes:</span>
                  <div className="flex gap-1">
                    {product.sizes.map((size) => (
                      <span
                        key={size}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4">
                  <button className="flex-1 btn-primary text-sm">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </button>
                  <button className="p-2 text-gray-400 hover:text-red-500 transition-colors duration-200">
                    <Heart className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Fit Recommendation Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 card-elevated p-8 bg-gradient-to-r from-blue-50 to-purple-50"
        >
          <div className="text-center">
            <div className="text-4xl mb-4">🎯</div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">AI-Powered Fit Recommendations</h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Based on your measurements, we recommend sizes and styles that will fit you perfectly. 
              Every product is marked with our confidence level.
            </p>
            <button className="btn-primary">
              <Shirt className="w-4 h-4 mr-2" />
              Get Your Measurements
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutPage; 